import { StyleSheet, type ViewStyle } from 'react-native'
import Animated, { Easing, FadeOutUp, Keyframe } from 'react-native-reanimated'

import { FigmaSuccessCheckIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Text, useTheme } from '../theme/restyle'

/**
 * The "Address copied" confirmation pill (Figma: Fun Mobile deposit toast). A
 * rounded pill with the label + a green success check. Surface + label track the
 * theme (`background/pill/default`, `content/elements/primary` via the medium
 * body cut) so it themes with light/dark; only the layout + soft shadow are
 * static (h42, px16, radius999, gap6).
 *
 * Purely presentational + self-animating on mount/unmount — it owns NO timer and
 * NO positioning. The parent (the deposit flow) mounts/unmounts it to show/hide
 * and absolutely-positions it; mounting plays the enter animation, unmounting
 * plays the exit. Built on `react-native-reanimated` layout animations, which
 * run inside the gorhom bottom-sheet the flow renders in.
 */

// Enter: slide up ~8px + fade in + scale 0.96→1 with a bouncy overshoot,
// matching the web motion spec (CSS cubic-bezier(0.34, 1.36, 0.64, 1) over
// ~450ms). The easing on the final keyframe drives the transition into it.
const enterAnimation = new Keyframe({
  0: {
    opacity: 0,
    transform: [{ translateY: 8 }, { scale: 0.96 }],
  },
  100: {
    opacity: 1,
    transform: [{ translateY: 0 }, { scale: 1 }],
    easing: Easing.bezier(0.34, 1.36, 0.64, 1),
  },
}).duration(450)

// Exit: fade + lift away over 350ms, matching the toast-close timing.
const exitAnimation = FadeOutUp.duration(350)

export function AddressCopiedToast({
  label: suppliedLabel,
}: { label?: string } = {}) {
  const { t } = useFunkitTranslation()
  const label = suppliedLabel ?? t('common.addressCopied')
  const theme = useTheme()
  return (
    <Animated.View
      entering={enterAnimation}
      exiting={exitAnimation}
      style={[styles.pill, { backgroundColor: theme.background.pill.default }]}
      accessibilityRole="alert"
      accessibilityLabel={label}
    >
      <Text variant="bodyMedium">{label}</Text>
      <FigmaSuccessCheckIcon size={16} />
    </Animated.View>
  )
}

// Layout + soft shadow only — the surface color comes from the theme's
// `background/pill/default` token, applied inline above.
const pill: ViewStyle = {
  height: 42,
  paddingHorizontal: 16,
  borderRadius: 999,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  shadowColor: '#000000',
  shadowOpacity: 0.08,
  shadowOffset: { width: 0, height: 4 },
  shadowRadius: 12,
}

const styles = StyleSheet.create({ pill })
